'use client';
import * as React from 'react';
import {
  Avatar,
  Box,
  Stack,
  Typography,
  List,
  ListItem,
  Divider,
  Button,
  Modal,
  ModalClose,
  ModalDialog,
  Grid,
  useBreakpoints,
  Link,
} from '@castlery/fortress';
import {
  logout,
  SaleUser,
  selectedAdminUserInfo,
  selectedAdminUserName,
  useGetSaleUsersQuery,
} from '@castlery/modules-user-domain';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import { useRouter } from 'next/navigation';
import { useAsyncFn } from 'react-use';
import { NextLink } from '@castlery/shared-components';
import { posRoutes, accessInAU } from '@castlery/config';
import { SalesRepLoginForm } from '../sales-rep-login-form/sales-rep-login-form';
import { selectedRetailId } from '@castlery/modules-retails-domain';
import { ShoppingBag, Account } from '@castlery/fortress/Icons';
import { cowboyLeadHelperLink } from './helper';

export const SwitchAdmin = ({ admin }: { admin: SaleUser }) => {
  const [open, setOpen] = React.useState(false);
  const retailId = useAppSelector(selectedRetailId);
  const router = useRouter();

  const handleAfterLogin = async () => {
    await router.replace(posRoutes.discover);
    await window.location.reload();
    await new Promise((resolve) => setTimeout(resolve, 1000));
  };

  return (
    <>
      <Stack
        direction={'column'}
        alignItems={'center'}
        justifyContent={'center'}
        onClick={() => {
          setOpen(true);
        }}
      >
        <Avatar size="lg">{admin.firstname.substring(0, 2).toUpperCase()}</Avatar>
        <Typography>{admin.firstname}</Typography>
      </Stack>
      <Modal
        aria-labelledby="modal-title"
        aria-describedby="modal-desc"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <ModalDialog
          sx={{
            padding: 0,
          }}
        >
          <SalesRepLoginForm
            showBackBtn={false}
            defaultValues={{
              retailId: retailId,
              email: admin.email,
            }}
            onSuccess={handleAfterLogin}
          />
        </ModalDialog>
      </Modal>
    </>
  );
};

export const AdminList = () => {
  const dispatch = useAppDispatch();
  const { mobile } = useBreakpoints();
  const adminUserInfo = useAppSelector(selectedAdminUserInfo);
  const name = useAppSelector(selectedAdminUserName);
  const { currentData: { results = [] } = {} } = useGetSaleUsersQuery(undefined, {});
  return (
    <>
      <Stack direction={mobile ? 'column' : 'row'} spacing={mobile ? 1 : 2}>
        <Stack justifyContent={'center'} alignItems={'center'} spacing={2}>
          <Typography>Current User</Typography>
          <Stack justifyContent={'center'} alignItems={'center'}>
            <Avatar size="lg">{adminUserInfo?.firstname.substring(0, 2).toUpperCase()}</Avatar>
            <Typography>{adminUserInfo?.email}</Typography>
            <Typography>{name}</Typography>
          </Stack>
          <Button
            variant="tertiary"
            color="danger"
            onClick={() => {
              dispatch(logout({}));
            }}
          >
            Log Out
          </Button>
        </Stack>
        <Grid
          container
          spacing={{ xs: 2, lg: 4 }}
          // columns={{ xs: 4, sm: 8, md: 12 }}
          sx={{
            flexGrow: 1,
            //  TODO 这里要优化一下
            overflow: 'auto', // 添加这一行
            maxHeight: '70vh', // 你可以设置一个最大高度
            paddingBottom: mobile ? 16 : '',
          }}
        >
          {results?.map((user) => (
            <Grid item xs={3} key={user.id}>
              <SwitchAdmin admin={user} />
            </Grid>
          ))}
        </Grid>
      </Stack>
    </>
  );
};

export const AdminListModal = () => {
  const name = useAppSelector(selectedAdminUserName);
  const { mobile } = useBreakpoints();
  const [open, setOpen] = React.useState<boolean>(false);

  return (
    <>
      <Stack
        onClick={() => {
          setOpen(true);
        }}
        role="button"
        direction={'column'}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <Avatar
          size="lg"
          sx={{
            '--Avatar-size': '5rem',
          }}
        >
          {name.substring(0, 2).toUpperCase()}
        </Avatar>
        <Typography
          sx={{
            marginTop: '20px',
          }}
        >
          {name}
        </Typography>
      </Stack>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <ModalDialog minWidth={600} layout={mobile ? 'fullscreen' : 'center'} size="lg">
          <ModalClose />
          <AdminList />
        </ModalDialog>
      </Modal>
    </>
  );
};

export function PosMenu() {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const [{ loading }, handleLogout] = useAsyncFn(async () => {
    await dispatch(logout({}));
    // router.refresh();
    await new Promise((resolve) => setTimeout(resolve, 1000));
  });
  return (
    <Box
      sx={{
        // height: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
      }}
    >
      <Stack
        gap={3}
        sx={(theme) => ({
          pt: theme.spacing(7),
          pb: theme.spacing(4),
        })}
      >
        <Stack direction={'column'} justifyContent={'center'} alignItems={'center'} spacing={2}>
          <AdminListModal />
        </Stack>
        <Divider />
        <List
          sx={{
            mx: 8,
            margin: '0 0 0 20px',
          }}
        >
          {[
            { label: 'Point-of-Sale', href: posRoutes.discover },
            {
              label: 'Sale History',
              href: posRoutes.saleHistory,
            },
            ...(accessInAU
              ? [
                  {
                    label: 'Guest',
                    href: '',
                  },
                ]
              : []),
          ].map(({ label, href }) => (
            <ListItem
              key={label}
              sx={{
                '&:hover': {
                  '& svg, & a, & button': {
                    color: 'var(--fortress-palette-brand-terracotta-700)',
                    textDecoration: 'none',
                  },
                  borderRight: '4px solid var(--fortress-palette-brand-terracotta-700)',
                },
                '& svg, & a,& button': {
                  color: 'var(--fortress-palette-brand-charcoal-800)',
                },
                borderRight: 'none',
                marginBottom: '10px',
              }}
            >
              {label === 'Guest' ? (
                <>
                  <Account />
                  <Link
                    component="button"
                    onClick={() => {
                      window?.open(cowboyLeadHelperLink, '_blank');
                    }}
                    sx={{ fontSize: 'md' }}
                  >
                    {label}
                  </Link>
                </>
              ) : (
                <>
                  <ShoppingBag />
                  <NextLink href={href}>{label}</NextLink>
                </>
              )}
            </ListItem>
          ))}
        </List>
      </Stack>

      <Stack
        sx={{
          // flex: 1,
          // justifyContent: 'flex-end',
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <Button
          variant="tertiary"
          onClick={() => {
            router.replace('/');
          }}
          sx={{
            color: 'var(--fortress-palette-brand-charcoal-500)',
            textDecoration: 'underline',
          }}
        >
          Change region
        </Button>
        <Button
          variant="tertiary"
          loading={loading}
          onClick={handleLogout}
          sx={{
            marginBottom: '1rem',
            color: 'var(--fortress-palette-brand-charcoal-500)',
            textDecoration: 'underline',
          }}
        >
          Logout
        </Button>
      </Stack>
    </Box>
  );
}

export default PosMenu;
